import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Collapse, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { format } from 'date-fns';

import type {
    CampaignNoticeKey,
    ElasticSearchAvailableFilters,
    ElasticSearchFilters,
    ElasticSearchGroupByFieldResponse,
} from 'c-sdk';
import { EsPCAReportField, EsPCAReportFilterField, PCAReportField } from 'c-sdk';

import apiClient from 'c-data/apiClient';

import type {
    ReportConfig,
    ReportDateSettings,
    ReportWrapperRefAPI,
    TableReportHeader,
    TableReportRow,
} from 'c-reports/Types';
import { ReportDateTimeframe, ReportType } from 'c-reports/Types';
import { ReportWrapper } from 'c-reports/Components';

import { performanceStatsToOldCampaignStats } from 'c-main/Components/Campaign/CampaignReports/ElasticSearch/Transformers';
import type { ReportTabProps } from 'c-main/Components/Campaign/CampaignReports/types';
import { useCommonTranslation } from 'c-translation';

import AnalyticsHourlyHistogramDialog from 'c-main/Components/Campaign/CampaignReports/ElasticSearch/AnalyticsHourlyHistogramDialog';
import type { AnalyticsLeaderboardDataRow } from './energyUsageUtils';
import {
    compareSortValues,
    DSP_AUDIENCE_FILTER_KEY,
    DSP_ENDPOINT_ALLOWED_FILTERS,
    DSP_NO_AUDIENCE_VALUE,
    foldGroupedDspStats,
    getAverageAudienceVals,
    getGeoVals,
    getRowFieldValue,
    isGroupedStatsLeaf,
    mergeAvailableFilters,
    normalizeLeaderboardRow,
    parseNum,
} from './energyUsageUtils';
import type {
    AnalyticsLeaderboardSortDir,
    AnalyticsLeaderboardSortKey,
} from './analyticsLeaderboardTableHelpers';
import {
    buildAnalyticsLeaderboardExportData,
    buildAnalyticsLeaderboardHeaders,
    buildAnalyticsLeaderboardRows,
} from './analyticsLeaderboardTableHelpers';
import type { DrilldownKey } from '../AnalyticsHourlyHistogramDialog';

type Props = {
    campaignId: number;
    field: EsPCAReportField;
    fields?: EsPCAReportField[];

    environment: any;
    source: any;
    showRoute?: boolean;

    currentStartDate: Date;
    currentEndDate: Date;
    currentTimeframe: ReportDateTimeframe;

    dateSettings: ReportDateSettings;
    reportWrapperRef: React.MutableRefObject<ReportWrapperRefAPI | undefined>;

    fieldFilters?: ElasticSearchFilters;
    refineFilters: boolean;

    geopathOnly: boolean;

    onFiltersUpdated: (filters: ElasticSearchAvailableFilters) => void;
    onResponse?: ReportTabProps['onResponse'];

    additionalControls?: React.ReactNode;
    advancedFilter?: React.ReactNode;
    showAdvancedFilters?: boolean;

    /** pass advancedFilters so the dialog can carry them over (if needed later) */
    advancedFilters?: ElasticSearchFilters;
    audienceMode?: 'standard' | 'averages';
    averagesAvailable?: boolean;
    foldBadKeysToOther?: boolean;
};

const nf0 = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const fmtWhole = (n?: number | null) => nf0.format(Math.round(n ?? 0));

const AnalyticsLeaderboardTable: React.FC<Props> = props => {
    const {
        campaignId,
        field,
        fields: explicitFields,
        environment,
        source,
        currentStartDate,
        currentEndDate,
        currentTimeframe,
        dateSettings,
        reportWrapperRef,
        fieldFilters,
        refineFilters,
        geopathOnly,
        showRoute = false,
        onFiltersUpdated,
        onResponse,
        additionalControls,
        advancedFilter,
        showAdvancedFilters = false,
        advancedFilters,
        audienceMode = 'standard',
        averagesAvailable = false,
        foldBadKeysToOther = false,
    } = props;
    const t = useCommonTranslation();
    const [pcaData, setPcaData] = useState<ElasticSearchGroupByFieldResponse | null>(null);
    const [dspLoading, setDspLoading] = useState(false);
    const [drilldown, setDrilldown] = useState<DrilldownKey | null>(null);
    const closeDrilldown = useCallback(() => setDrilldown(null), []);
    const isAverageAudienceMode = audienceMode === 'averages';
    const dspAbortRef = useRef<AbortController | null>(null);
    const latestDspRequestKeyRef = useRef<string | null>(null);

    const [sortKey, setSortKey] = useState<AnalyticsLeaderboardSortKey>(null);
    const [sortDir, setSortDir] = useState<AnalyticsLeaderboardSortDir>('desc');
    const [topBottomFilter, setTopBottomFilter] = useState<'top' | 'bottom' | 'none'>('none');
    const [topBottomCount, setTopBottomCount] = useState(5);
    const showDisplayFormatColumn = field === EsPCAReportField.DisplayOwnerFormat;
    const isOwnerFormatGroup = field === EsPCAReportField.DisplayOwnerFormat;

    const toggleSort = useCallback((k: AnalyticsLeaderboardSortKey) => {
        setSortKey(prev => {
            if (prev !== k) {
                setSortDir('desc');
                return k;
            }
            setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
            return prev;
        });
    }, []);

    const dspFieldFilters = useMemo(() => {
        const next = { ...(fieldFilters ?? {}) };
        const selectedAudience = (next[DSP_AUDIENCE_FILTER_KEY] ?? []).filter(
            v => v != null && v !== '',
        );

        delete next[DSP_AUDIENCE_FILTER_KEY];
        delete next.audience_version;
        delete next.scheduled;
        delete next.display_type;

        if (selectedAudience.length === 0) {
            if (isAverageAudienceMode) {
                next[EsPCAReportFilterField.HasAudienceData] = [0];
            }
            return next;
        }

        next[EsPCAReportFilterField.HasAudienceData] = [
            selectedAudience.includes(DSP_NO_AUDIENCE_VALUE) ? 0 : 1,
        ];

        return next;
    }, [fieldFilters, isAverageAudienceMode]);

    const isMultiField = Array.isArray(explicitFields) && explicitFields.length > 1;
    const isMultiFieldOwnerFormat = useMemo(() => {
        if (!isMultiField || (explicitFields ?? []).length !== 2) return false;
        const set = new Set(explicitFields);
        return set.has(EsPCAReportField.DisplayOwner) && set.has(EsPCAReportField.DisplayFormat);
    }, [explicitFields, isMultiField]);
    const shouldUseDspEndpoint = useMemo(() => {
        if (isMultiField && !isMultiFieldOwnerFormat) return false;

        const hasUnsupportedFilters = Object.entries(fieldFilters ?? {}).some(([key, values]) => {
            if (!Array.isArray(values) || values.length === 0) return false;
            return !DSP_ENDPOINT_ALLOWED_FILTERS.has(key);
        });
        if (hasUnsupportedFilters) return false;

        const ownerFormatFilters = fieldFilters?.[EsPCAReportFilterField.DisplayOwnerFormat] ?? [];
        const selectedAudience = (fieldFilters?.[DSP_AUDIENCE_FILTER_KEY] ?? []).filter(
            v => v != null && v !== '',
        );
        const isNothingAudience =
            selectedAudience.length === 1 && selectedAudience[0] === DSP_NO_AUDIENCE_VALUE;
        const hasAuditedAudienceSelected = selectedAudience.length > 0 && !isNothingAudience;

        const groupedByOwnerOrFormat =
            isMultiFieldOwnerFormat ||
            field === EsPCAReportField.DisplayOwner ||
            field === EsPCAReportField.DisplayFormat ||
            field === EsPCAReportField.DisplayOwnerFormat;
        const filteredByOwnerOrFormat =
            (fieldFilters?.[EsPCAReportFilterField.DisplayOwner]?.length ?? 0) > 0 ||
            (fieldFilters?.[EsPCAReportFilterField.DisplayFormat]?.length ?? 0) > 0 ||
            ownerFormatFilters.length > 0;

        if (hasAuditedAudienceSelected) {
            return groupedByOwnerOrFormat || ownerFormatFilters.length > 0;
        }

        if (isNothingAudience) {
            return groupedByOwnerOrFormat || filteredByOwnerOrFormat;
        }

        return false;
    }, [field, fieldFilters, isMultiField, isMultiFieldOwnerFormat]);

    const dspFields = useMemo(() => {
        if (isMultiField) return explicitFields as EsPCAReportField[];
        return field === EsPCAReportField.DisplayOwnerFormat
            ? [EsPCAReportField.DisplayOwner, EsPCAReportField.DisplayFormat]
            : [field ?? EsPCAReportField.DisplayOwner];
    }, [explicitFields, field, isMultiField]);

    const dialogFieldFilters = useMemo<Record<string, string[]>>(
        () =>
            Object.fromEntries(
                Object.entries(fieldFilters ?? {}).map(([key, values]) => [
                    key,
                    (values ?? []).map(value => String(value)),
                ]),
            ),
        [fieldFilters],
    );
    const selectedAudienceLabel = useMemo(() => {
        const values = (fieldFilters?.[DSP_AUDIENCE_FILTER_KEY] ?? [])
            .map(value => String(value))
            .filter(value => value !== '' && value !== DSP_NO_AUDIENCE_VALUE);

        return values.length > 0 ? values.join(', ') : null;
    }, [fieldFilters]);

    const fetchData: ReportConfig<ElasticSearchGroupByFieldResponse>['fetchData'] = useCallback(
        (start, end, timeframe) =>
            apiClient.ReportsElasticSearch.pcaGroupByField({
                campaignId,
                showStats: true,
                startDate:
                    timeframe === ReportDateTimeframe.All
                        ? undefined
                        : `${format(start, 'yyyy-MM-dd')} 00:00:00`,
                endDate:
                    timeframe === ReportDateTimeframe.All
                        ? undefined
                        : `${format(end, 'yyyy-MM-dd')} 23:59:59`,
                fields: dspFields,
                environment,
                source,
                fieldFilters,
                dataStatus: fieldFilters?.[PCAReportField.DataStatus] as CampaignNoticeKey[],
            }),
        [campaignId, dspFields, environment, fieldFilters, source],
    );

    const onDataUpdated: ReportConfig<ElasticSearchGroupByFieldResponse>['onDataUpdated'] =
        useCallback(
            async (data, start, end, timeframe) => {
                if (!data) return;

                if (!shouldUseDspEndpoint) {
                    setPcaData(data);
                }
                onFiltersUpdated((data?.filters ?? {}) as any);

                if (shouldUseDspEndpoint) {
                    const dspStartDate =
                        timeframe === ReportDateTimeframe.All
                            ? undefined
                            : `${format(start, 'yyyy-MM-dd')} 00:00:00`;
                    const dspEndDate =
                        timeframe === ReportDateTimeframe.All
                            ? undefined
                            : `${format(end, 'yyyy-MM-dd')} 23:59:59`;
                    const dspRequestKey = JSON.stringify({
                        campaignId,
                        fields: dspFields,
                        startDate: dspStartDate,
                        endDate: dspEndDate,
                        environment,
                        source,
                        fieldFilters: dspFieldFilters,
                    });

                    if (latestDspRequestKeyRef.current === dspRequestKey) return;
                    latestDspRequestKeyRef.current = dspRequestKey;

                    dspAbortRef.current?.abort();
                    const controller = new AbortController();
                    dspAbortRef.current = controller;
                    setDspLoading(true);
                    try {
                        const dspResponse = await apiClient.ReportsElasticSearch.dsp(
                            {
                                campaignId,
                                showStats: true,
                                showFilters: true,
                                fields: dspFields,
                                startDate: dspStartDate,
                                endDate: dspEndDate,
                                environment,
                                source,
                                fieldFilters: dspFieldFilters,
                                dataStatus: dspFieldFilters?.[
                                    PCAReportField.DataStatus
                                ] as CampaignNoticeKey[],
                            },
                            { signal: controller.signal },
                        ).request;

                        if (controller.signal.aborted) return;
                        setPcaData({
                            ...dspResponse.data,
                            filters: mergeAvailableFilters(
                                {
                                    ...(data?.filters ?? {}),
                                    [DSP_AUDIENCE_FILTER_KEY]:
                                        data?.filters?.[DSP_AUDIENCE_FILTER_KEY],
                                },
                                dspResponse.data?.filters,
                            ) as ElasticSearchAvailableFilters,
                            campaign_stats:
                                data?.campaign_stats ?? dspResponse.data?.campaign_stats,
                        });

                        if (data?.campaign_stats && onResponse) {
                            onResponse(
                                {
                                    campaignStats: performanceStatsToOldCampaignStats(
                                        data.campaign_stats,
                                    ),
                                    filters: (data?.filters ?? {}) as any,
                                },
                                start,
                                end,
                                timeframe,
                            );
                        }
                    } catch (error) {
                        if (controller.signal.aborted) return;
                        latestDspRequestKeyRef.current = null;
                        setPcaData(data);
                        if (data?.campaign_stats && onResponse) {
                            onResponse(
                                {
                                    campaignStats: performanceStatsToOldCampaignStats(
                                        data.campaign_stats,
                                    ),
                                    filters: (data?.filters ?? {}) as any,
                                },
                                start,
                                end,
                                timeframe,
                            );
                        }
                    } finally {
                        if (dspAbortRef.current === controller) {
                            setDspLoading(false);
                        }
                    }
                } else if (data?.campaign_stats && onResponse) {
                    onResponse(
                        {
                            campaignStats: performanceStatsToOldCampaignStats(data.campaign_stats),
                            filters: (data?.filters ?? {}) as any,
                        },
                        start,
                        end,
                        timeframe,
                    );
                }
            },
            [
                campaignId,
                dspFieldFilters,
                dspFields,
                environment,
                onFiltersUpdated,
                onResponse,
                shouldUseDspEndpoint,
                source,
            ],
        );

    const fieldsKey = useMemo(() => (explicitFields ?? []).join(','), [explicitFields]);
    const requestKeyPrefix = useMemo(
        () =>
            `analytics-${campaignId}-${String(field)}-${fieldsKey}-${geopathOnly ? 'geo' : 'mix'}`,
        [campaignId, field, fieldsKey, geopathOnly],
    );

    useEffect(() => {
        latestDspRequestKeyRef.current = null;
        setPcaData(null);
        reportWrapperRef?.current?.refreshData?.();
    }, [field, fieldsKey, geopathOnly, isAverageAudienceMode, reportWrapperRef]);

    const fold = useCallback(
        (value: any) => (foldBadKeysToOther ? foldGroupedDspStats(value) : value),
        [foldBadKeysToOther],
    );

    const grouped = useMemo(() => {
        const anyData: any = pcaData as any;
        return fold(
            anyData?.data ?? anyData?.reportData ?? anyData?.report_data ?? anyData?.buckets ?? {},
        );
    }, [pcaData, fold]);

    const auditMultipliers = useMemo(() => {
        const anyData: any = pcaData as any;
        const value =
            anyData?.auditMultipliers ??
            anyData?.audit_multipliers ??
            anyData?.auditMultipliersData ??
            null;
        return value == null ? null : fold(value);
    }, [pcaData, fold]);

    const multipliers = useMemo(() => {
        const anyData: any = pcaData as any;
        const value = anyData?.multipliers ?? anyData?.multipliersData ?? null;
        return value == null ? null : fold(value);
    }, [pcaData, fold]);

    const auditRowsData = useMemo<AnalyticsLeaderboardDataRow[]>(() => {
        if (!auditMultipliers) return [];

        if (Array.isArray(auditMultipliers)) {
            return auditMultipliers.map((item: any, idx: number) =>
                normalizeLeaderboardRow(field, item, idx),
            );
        }

        if (auditMultipliers && typeof auditMultipliers === 'object') {
            return Object.entries(auditMultipliers as Record<string, any>).map(
                ([key, stats], idx) => normalizeLeaderboardRow(field, stats, idx, { key }),
            );
        }

        return [];
    }, [auditMultipliers, field]);

    const selectedAudienceValues = useMemo(
        () =>
            (fieldFilters?.[DSP_AUDIENCE_FILTER_KEY] ?? []).filter(
                v => v != null && String(v) !== '',
            ),
        [fieldFilters],
    );
    const isNonAuditedAudience = useMemo(
        () =>
            selectedAudienceValues.length === 1 &&
            String(selectedAudienceValues[0]) === DSP_NO_AUDIENCE_VALUE,
        [selectedAudienceValues],
    );
    const hasAuditedAudience = useMemo(
        () => selectedAudienceValues.length > 0 && !isNonAuditedAudience,
        [isNonAuditedAudience, selectedAudienceValues],
    );
    const showAuditDspColumns = shouldUseDspEndpoint && hasAuditedAudience;

    const nonAuditedRowsData = useMemo<AnalyticsLeaderboardDataRow[]>(() => {
        if (!multipliers) return [];

        if (Array.isArray(multipliers)) {
            return multipliers.map((item: any, idx: number) =>
                normalizeLeaderboardRow(field, item, idx),
            );
        }

        if (multipliers && typeof multipliers === 'object') {
            return Object.entries(multipliers as Record<string, any>).map(([key, stats], idx) =>
                normalizeLeaderboardRow(field, stats, idx, { key }),
            );
        }

        return [];
    }, [multipliers, field]);

    const showNonAuditedDspColumns = shouldUseDspEndpoint && !hasAuditedAudience;

    const useAuditMultipliers = showAuditDspColumns && auditRowsData.length > 0;
    const useNonAuditedMultipliers = showNonAuditedDspColumns && nonAuditedRowsData.length > 0;

    const useMultiplierTable = shouldUseDspEndpoint;

    const impressionsColumnLabel = t('Modules.Main.Campaigns.Overview.dsp.dspImpressions');

    const rowsData = useMemo<AnalyticsLeaderboardDataRow[]>(() => {
        if (grouped && typeof grouped === 'object' && !Array.isArray(grouped)) {
            if (isMultiField) {
                const out: AnalyticsLeaderboardDataRow[] = [];
                const walk = (node: any, depth: number, parts: string[]) => {
                    if (!node || typeof node !== 'object') return;
                    if (depth >= dspFields.length || isGroupedStatsLeaf(node)) {
                        const key = parts.join(' | ');
                        const extras: Record<string, any> = { key };
                        dspFields.forEach((f, i) => {
                            extras[f as string] = parts[i];
                        });
                        out.push(normalizeLeaderboardRow(field, node, out.length, extras));
                        return;
                    }
                    Object.entries(node).forEach(([k, v]) => {
                        walk(v, depth + 1, [...parts, k]);
                    });
                };
                walk(grouped, 0, []);
                return out;
            }

            if (field === EsPCAReportField.DisplayOwnerFormat) {
                return Object.entries(grouped as Record<string, any>).flatMap(
                    ([ownerKey, value]) => {
                        if (isGroupedStatsLeaf(value)) {
                            return [
                                normalizeLeaderboardRow(field, value, 0, {
                                    owner: ownerKey,
                                    key: ownerKey,
                                }),
                            ];
                        }

                        if (value && typeof value === 'object' && !Array.isArray(value)) {
                            return Object.entries(value as Record<string, any>).map(
                                ([formatKey, stats], idx) =>
                                    normalizeLeaderboardRow(field, stats, idx, {
                                        owner: ownerKey,
                                        format: formatKey,
                                        key: `${ownerKey}__${formatKey}`,
                                    }),
                            );
                        }

                        return [];
                    },
                );
            }

            return Object.entries(grouped as Record<string, any>).map(([key, stats], idx) =>
                normalizeLeaderboardRow(field, stats, idx, { key }),
            );
        }

        if (Array.isArray(grouped)) {
            return grouped.map((item: any, idx: number) =>
                normalizeLeaderboardRow(field, item, idx),
            );
        }

        return [];
    }, [grouped, field, dspFields, isMultiField]);

    const getAvailableCountForFilter = useCallback(
        (filter: 'top' | 'bottom' | 'none') => {
            const baseRows = useAuditMultipliers
                ? auditRowsData
                : useNonAuditedMultipliers
                ? nonAuditedRowsData
                : rowsData;

            const visibleRows = baseRows.filter(row => {
                const key = getRowFieldValue(row, field);
                return String(key).trim() !== '';
            });

            if (filter === 'none') {
                return visibleRows.length;
            }

            return visibleRows.filter(row => {
                const variance = parseNum(row.variance);
                return (filter === 'top' && variance > 0) || (filter === 'bottom' && variance < 0);
            }).length;
        },
        [
            auditRowsData,
            field,
            nonAuditedRowsData,
            rowsData,
            useAuditMultipliers,
            useNonAuditedMultipliers,
        ],
    );

    const topBottomAvailableCount = useMemo(
        () => getAvailableCountForFilter(topBottomFilter),
        [getAvailableCountForFilter, topBottomFilter],
    );

    useEffect(() => {
        if (!useMultiplierTable || topBottomFilter === 'none' || topBottomFilter == null) return;

        setTopBottomCount(curr =>
            curr === 5 || curr > topBottomAvailableCount
                ? Math.max(1, topBottomAvailableCount)
                : curr,
        );
    }, [topBottomAvailableCount, topBottomFilter, useMultiplierTable]);

    const sortedRowsData = useMemo(() => {
        const baseRows = useAuditMultipliers
            ? auditRowsData
            : useNonAuditedMultipliers
            ? nonAuditedRowsData
            : rowsData;
        let tempRows = baseRows.filter(row => {
            const key = getRowFieldValue(row, field);
            return String(key).trim() !== '';
        });

        if (useMultiplierTable && topBottomFilter && topBottomFilter !== 'none') {
            tempRows = tempRows.filter(row => {
                const variance = parseNum(row.variance);
                return (
                    (topBottomFilter === 'top' && variance > 0) ||
                    (topBottomFilter === 'bottom' && variance < 0)
                );
            });

            tempRows = [...tempRows].sort((a, b) => {
                const da = parseNum(a?.dsp_impressions ?? 0);
                const db = parseNum(b?.dsp_impressions ?? 0);
                return db - da;
            });

            tempRows = tempRows.slice(0, topBottomCount);
        }

        if (!sortKey) return tempRows;

        const dir = sortDir === 'asc' ? 1 : -1;

        const getSortValue = (row: AnalyticsLeaderboardDataRow) => {
            const fieldValue = getRowFieldValue(row, field);
            const rawDisplayFormat =
                row.display_format ??
                row.display_format_name ??
                row.displayFormat ??
                row.format ??
                '';
            const ownerLabel = row.display_owner ?? row.displayOwner ?? row.owner ?? fieldValue;

            switch (sortKey) {
                case 'field':
                    if (useAuditMultipliers || useNonAuditedMultipliers) return String(ownerLabel);
                    if (field === EsPCAReportField.Hour) return parseNum(fieldValue);
                    return String(fieldValue);
                case 'displayFormat':
                    return String(rawDisplayFormat);
                case 'dspImpressions':
                    return parseNum(row.dsp_impressions);
                case 'geoImpressions':
                    if (isAverageAudienceMode) return getAverageAudienceVals(row).avg;
                    return parseNum(row.geopath_impressions);
                case 'variance':
                    if (isAverageAudienceMode) return getAverageAudienceVals(row).variance;
                    return useAuditMultipliers || useNonAuditedMultipliers
                        ? parseNum(row.variance)
                        : getGeoVals(row).variance;
                case 'performance':
                    if (isAverageAudienceMode) return getAverageAudienceVals(row).performancePct;
                    return getGeoVals(row).performancePct;
                case 'dspIm':
                    return parseNum(row.dsp_avg_impressions);
                case 'geoIm':
                    return parseNum(row.geopath_avg_impressions);
                case 'avgIm':
                    return parseNum(row.average_im);
                case 'imVar':
                    return useNonAuditedMultipliers
                        ? parseNum(row.variance ?? row.im_variance)
                        : parseNum(row.im_variance);
                case 'pctVar':
                    return parseNum(row.variance_percent) * 100;
                case 'totalPlays':
                    return parseNum(row.sum_of_plays ?? row.plays ?? 0);
                case 'totalImpressions':
                    return parseNum(row.impacts ?? row.impressions ?? 0);
                case 'avgDspIm': {
                    const totalPlays = parseNum(row.sum_of_plays ?? row.plays ?? 0);
                    const totalImpressions = parseNum(row.impacts ?? row.impressions ?? 0);
                    return totalPlays > 0 ? totalImpressions / totalPlays : 0;
                }
                default:
                    return '';
            }
        };

        return [...tempRows].sort((a, b) => {
            const aVal = getSortValue(a);
            const bVal = getSortValue(b);
            return compareSortValues(aVal, bVal) * dir;
        });
    }, [
        field,
        rowsData,
        auditRowsData,
        nonAuditedRowsData,
        useAuditMultipliers,
        useNonAuditedMultipliers,
        useMultiplierTable,
        isAverageAudienceMode,
        sortKey,
        sortDir,
        topBottomFilter,
        topBottomCount,
    ]);

    const headers = useMemo<TableReportHeader[]>(
        () =>
            buildAnalyticsLeaderboardHeaders({
                t,
                field,
                showRoute,
                showDisplayFormatColumn,
                geopathOnly,
                isAverageAudienceMode,
                sortKey,
                sortDir,
                showAuditDspColumns,
                hasAuditedAudience,
                selectedAudienceLabel,
                showNonAuditedDspColumns,
                toggleSort,
                isOwnerFormatGroup,
                groupFields: isMultiField ? dspFields : undefined,
            }),
        [
            t,
            field,
            showRoute,
            showDisplayFormatColumn,
            geopathOnly,
            isAverageAudienceMode,
            sortKey,
            sortDir,
            showAuditDspColumns,
            hasAuditedAudience,
            selectedAudienceLabel,
            showNonAuditedDspColumns,
            toggleSort,
            isOwnerFormatGroup,
            isMultiField,
            dspFields,
        ],
    );

    const tableRows = useMemo<TableReportRow[]>(
        () =>
            buildAnalyticsLeaderboardRows({
                sortedRowsData,
                field,
                geopathOnly,
                isAverageAudienceMode,
                isOwnerFormatGroup,
                showDisplayFormatColumn,
                showAuditDspColumns,
                showNonAuditedDspColumns,
                useMultiplierTable,
                setDrilldown,
                groupFields: isMultiField ? dspFields : undefined,
            }),
        [
            sortedRowsData,
            field,
            geopathOnly,
            isAverageAudienceMode,
            isOwnerFormatGroup,
            showDisplayFormatColumn,
            showAuditDspColumns,
            showNonAuditedDspColumns,
            useMultiplierTable,
            isMultiField,
            dspFields,
        ],
    );

    const exportData = useMemo(
        () =>
            buildAnalyticsLeaderboardExportData({
                t,
                field,
                showRoute,
                showDisplayFormatColumn,
                geopathOnly,
                isAverageAudienceMode,
                showAuditDspColumns,
                hasAuditedAudience,
                selectedAudienceLabel,
                showNonAuditedDspColumns,
                isOwnerFormatGroup,
                sortedRowsData,
                groupFields: isMultiField ? dspFields : undefined,
            }),
        [
            t,
            field,
            showRoute,
            showDisplayFormatColumn,
            geopathOnly,
            isAverageAudienceMode,
            showAuditDspColumns,
            hasAuditedAudience,
            selectedAudienceLabel,
            showNonAuditedDspColumns,
            isOwnerFormatGroup,
            sortedRowsData,
            isMultiField,
            dspFields,
        ],
    );

    const reportProps = useMemo(
        () => [
            {
                type: ReportType.Table,
                namePrefix: `${campaignId}_details_${field}_${
                    geopathOnly ? 'geopath' : 'standard'
                }`,
                data: exportData,
                headers,
                rows: tableRows,
                disableVirtualization: true,
            },
        ],
        [campaignId, exportData, field, geopathOnly, headers, tableRows],
    );

    const beforeChartComponent = useMemo(
        () => (
            <>
                {useMultiplierTable && (
                    <Box display="flex" gap={2} alignItems="center" sx={{ mb: 1 }}>
                        <FormControl sx={{ minWidth: 220 }}>
                            {!topBottomFilter && (
                                <InputLabel>
                                    {t('Modules.Main.Campaigns.Overview.dsp.value')}
                                </InputLabel>
                            )}
                            <Select
                                value={topBottomFilter}
                                onChange={e => {
                                    const nextFilter = (e.target.value as any) ?? null;
                                    setTopBottomFilter(nextFilter);
                                    if (nextFilter !== 'none' && nextFilter != null) {
                                        setTopBottomCount(
                                            Math.max(1, getAvailableCountForFilter(nextFilter)),
                                        );
                                    }
                                }}
                                displayEmpty
                                size="small"
                                IconComponent={topBottomFilter ? () => null : undefined}
                                sx={{ minWidth: '15rem' }}
                            >
                                {topBottomFilter !== null || topBottomFilter === 'none' ? (
                                    <MenuItem value="none">
                                        {t('Modules.Main.Campaigns.Overview.dsp.value')}
                                    </MenuItem>
                                ) : null}
                                <MenuItem value="top">
                                    {t('Modules.Main.Campaigns.Overview.dsp.top')}
                                </MenuItem>
                                <MenuItem value="bottom">
                                    {t('Modules.Main.Campaigns.Overview.dsp.bottom')}
                                </MenuItem>
                            </Select>
                        </FormControl>

                        {topBottomFilter !== 'none' && topBottomFilter != null && (
                            <TextField
                                type="number"
                                value={topBottomCount}
                                onChange={e =>
                                    setTopBottomCount(
                                        Math.min(
                                            Math.max(1, Number(e.target.value)),
                                            Math.max(1, topBottomAvailableCount),
                                        ),
                                    )
                                }
                                size="small"
                                InputProps={{
                                    inputProps: {
                                        min: 1,
                                        max: Math.max(1, topBottomAvailableCount),
                                    },
                                }}
                                label={t('Modules.Main.Campaigns.Overview.dsp.count')}
                                sx={{ width: 120 }}
                            />
                        )}
                    </Box>
                )}
                {advancedFilter && (
                    <Collapse in={showAdvancedFilters}>
                        <Box>{advancedFilter}</Box>
                    </Collapse>
                )}
            </>
        ),
        [
            advancedFilter,
            showAdvancedFilters,
            useMultiplierTable,
            getAvailableCountForFilter,
            topBottomAvailableCount,
            topBottomFilter,
            topBottomCount,
            t,
        ],
    );

    return (
        <>
            <ReportWrapper
                ref={reportWrapperRef as any}
                requestKeyPrefix={requestKeyPrefix}
                initialStartDate={currentStartDate}
                initialEndDate={currentEndDate}
                dateSettings={dateSettings}
                reportChartWrapperProps={{ height: '75vh' }}
                reportWrapperProps={{
                    sx: {
                        bgcolor: 'background.paper',
                        mt: 1,
                        pb: 1,
                        pt: 1,
                    },
                }}
                additionalDateControls={<>{additionalControls}</>}
                additionalDateControlsPosition="end"
                beforeChartComponent={beforeChartComponent}
                fetchData={fetchData}
                onDataUpdated={onDataUpdated}
                reportProps={reportProps as any}
                externalLoading={dspLoading}
            />

            <AnalyticsHourlyHistogramDialog
                open={drilldown != null}
                onClose={closeDrilldown}
                campaignId={campaignId}
                environment={environment}
                source={source}
                startDate={currentStartDate}
                endDate={currentEndDate}
                fieldFilters={dialogFieldFilters}
                geopathOnly={geopathOnly}
                showRoute={showRoute}
                audienceMode={audienceMode}
                averagesAvailable={averagesAvailable}
                selectedAudienceLabel={selectedAudienceLabel}
                impressionsColumnLabel={impressionsColumnLabel}
                drilldown={drilldown}
                ignoredFilterKeys={['display_type']}
            />
        </>
    );
};

export default AnalyticsLeaderboardTable;
